import axios from "axios"

/**
 * Creates an axios instance that uses a specific token for requests
 * @param {string} token - The access token to use for requests
 * @returns {AxiosInstance} An axios instance configured with the provided token
 */
const createAxiosWithCustomToken = (token) => {
    const instance = axios.create({
      baseURL: import.meta.env.VITE_FACEBOOK_BASE_URL,
      headers: { "Content-Type": "application/json" },
    });
  
    // Request interceptor
    instance.interceptors.request.use(
      (config) => {
        if (token) {
          config.params = {
            ...config.params,
            access_token: token,
          };
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  
    // Response interceptor
    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          error.response &&
          error.response.data &&
          error.response.data.message === "Invalid or expired token"
        ) {
          console.error("Token validation error:", error.response.data);
        }
        return Promise.reject(error);
      }
    );
  
    return instance;
};
  
/**
 * Axios instance that accepts a token parameter with each request
 * This allows using different tokens for different requests
 */
export const axiosWithCustomToken = {
  get: (url, token, options = {}) => {
    return createAxiosWithCustomToken(token).get(url, options);
  },
  post: (url, data, token, options = {}) => {
    return createAxiosWithCustomToken(token).post(url, data, options);
  },
  put: (url, data, token, options = {}) => {
    return createAxiosWithCustomToken(token).put(url, data, options);
  },
  delete: (url, token, options = {}) => {
    return createAxiosWithCustomToken(token).delete(url, options);
  },
  patch: (url, data, token, options = {}) => {
    return createAxiosWithCustomToken(token).patch(url, data, options);
  }
};

// Export both - the factory function and the convenience object
export default axiosWithCustomToken;